/**
 * Arm-state slice — the planner's view of the arm (PROTOCOL.md §4.6/§4.7): measured joint
 * positions from `joint-state` (≈30 Hz, DataChannel `telemetry`), planner mode, the active
 * goal, and the last goal event (`robot-event` with a `goal-*` code, channel `events`).
 *
 * Framework-free; ingests raw DataChannel messages like the other slices. `joint-state`
 * arrives at a high rate: consumers that only need the mode/goal should subscribe with a
 * selector, and the 3D view reads joints through `subscribe` outside React rendering.
 */

import { createStore } from "./store";
import type { StoreApi } from "./store";

export type PlannerMode = "hold" | "manual" | "goal";

export interface GoalState {
  goal_id: string;
  name: string;
  state: string;
  /** 0…1 */
  progress: number;
}

export interface JointState {
  /** Sample time, ms, planner wall clock. */
  t: number;
  model: string;
  names: string[];
  /** Radians (metres for prismatic joints), same order as `names`. */
  position: number[];
  /** Optional base pose in odom/world frame (m, m, rad). */
  base_pose?: { x: number; y: number; yaw: number };
  mode: PlannerMode;
  goal: GoalState | null;
}

export interface GoalEvent {
  code: string;
  detail: string;
  goal_id: string | null;
  level: string;
  /** Local receive time (ms, Date.now()). */
  at: number;
}

export interface ArmStateSnapshot {
  joints: JointState | null;
  /** Local receive time of `joints` (ms, Date.now()). */
  receivedAt: number | null;
  lastGoalEvent: GoalEvent | null;
}

export interface ArmStateSlice extends Pick<
  StoreApi<ArmStateSnapshot>,
  "getState" | "getInitialState" | "subscribe"
> {
  ingest(message: unknown): void;
  reset(): void;
}

function parse(message: unknown): Record<string, unknown> | null {
  let raw: unknown = message;
  if (typeof raw !== "string") {
    if (raw instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(raw));
    else if (ArrayBuffer.isView(raw)) raw = new TextDecoder().decode(raw);
    else return null;
  }
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function normalizeGoal(raw: unknown): GoalState | null {
  if (!raw || typeof raw !== "object") return null;
  const g = raw as Record<string, unknown>;
  if (typeof g.goal_id !== "string" || typeof g.name !== "string") return null;
  const progress = Number(g.progress);
  return {
    goal_id: g.goal_id,
    name: g.name,
    state: String(g.state ?? "active"),
    progress: Number.isFinite(progress) ? Math.min(1, Math.max(0, progress)) : 0,
  };
}

/** Returns null for a malformed payload (names/position missing or of different length). */
export function normalizeJointState(p: Record<string, unknown>): JointState | null {
  if (!Array.isArray(p.names) || !Array.isArray(p.position)) return null;
  if (p.names.length !== p.position.length) return null;
  const position = p.position.map(Number);
  if (position.some((v) => !Number.isFinite(v))) return null;
  const mode = p.mode === "manual" || p.mode === "goal" ? p.mode : "hold";
  const rawPose = p.base_pose && typeof p.base_pose === "object"
    ? p.base_pose as Record<string, unknown>
    : null;
  const coords = rawPose ? [Number(rawPose.x), Number(rawPose.y), Number(rawPose.yaw)] : [];
  const base_pose = coords.length === 3 && coords.every(Number.isFinite)
    ? { x: coords[0]!, y: coords[1]!, yaw: coords[2]! }
    : undefined;
  return {
    t: typeof p.t === "number" ? p.t : 0,
    model: String(p.model ?? ""),
    names: p.names.map(String),
    position,
    ...(base_pose ? { base_pose } : {}),
    mode,
    goal: normalizeGoal(p.goal),
  };
}

const EMPTY: ArmStateSnapshot = { joints: null, receivedAt: null, lastGoalEvent: null };

export function createArmStateSlice(now: () => number = Date.now): ArmStateSlice {
  const store = createStore<ArmStateSnapshot>(() => EMPTY);

  return {
    getState: store.getState,
    getInitialState: store.getInitialState,
    subscribe: store.subscribe,

    ingest(message) {
      const msg = parse(message);
      if (!msg) return;
      const payload = msg.payload;
      if (!payload || typeof payload !== "object") return;
      const p = payload as Record<string, unknown>;
      if (msg.event === "joint-state") {
        const joints = normalizeJointState(p);
        if (joints) store.setState({ joints, receivedAt: now() });
      } else if (msg.event === "robot-event") {
        const code = String(p.code ?? "");
        if (!code.startsWith("goal-")) return;
        store.setState({
          lastGoalEvent: {
            code,
            detail: String(p.detail ?? ""),
            goal_id: typeof p.goal_id === "string" ? p.goal_id : null,
            level: String(p.level ?? "info"),
            at: now(),
          },
        });
      }
    },

    reset() {
      store.setState(EMPTY, true);
    },
  };
}
